import type { AnyAction, Middleware, MiddlewareAPI } from 'redux';
import { initActionTypes } from '@makemydeal/dr-shared-store';
import { decompressPayload } from '@makemydeal/dr-common-utils';
import { findPaymentGridPayloadInBaggage, resolvePaymentGridForScenario } from '@makemydeal/dr-platform-shared';
import type { BaggageEntry } from '@makemydeal/dr-platform-shared';
import { Actions } from '../actionTypes/actionTypes';
import { getActiveDeal, getPublishedVersion } from '../activeDeal/selector';
import { isMultiScenarioOfferEnabled } from '../dealOffer/selector';
import type { SalesCheckinRootState } from '../common/types/SalesCheckinRootState';
import { resetPaymentGrids, setPaymentGrids } from './paymentGridsActionCreators';
import type { ScenarioPaymentGrid } from './types';

// Grids live in a baggage item named for the published comparison version, one entry per scenario.
const getPublishedPaymentGrids = (
    baggage: BaggageEntry[] | undefined,
    publishedVersion: string
): Record<string, ScenarioPaymentGrid> => {
    const payload = findPaymentGridPayloadInBaggage(baggage, publishedVersion);
    if (!payload) return {};

    // Map every scenario to its resolved grid, the published version's scenario is primary.
    const grids: Record<string, ScenarioPaymentGrid> = {};
    for (const { id } of payload.scenarios) {
        const { grid } = resolvePaymentGridForScenario({ scenario: { id }, payload }, decompressPayload);
        if (grid) {
            grids[id] = { scenarioId: id, isPrimary: id === publishedVersion, grid };
        }
    }
    return grids;
};

const loadPublishedPaymentGrids = (store: MiddlewareAPI) => {
    const state = store.getState() as SalesCheckinRootState;
    if (!isMultiScenarioOfferEnabled(state)) return;
    // No published offer or no grids: clear any grids left over from a previously loaded deal.
    const publishedVersion = getPublishedVersion(state);
    const scenarios = publishedVersion
        ? getPublishedPaymentGrids(getActiveDeal(state).baggage as BaggageEntry[] | undefined, publishedVersion)
        : {};
    store.dispatch(
        publishedVersion && Object.keys(scenarios).length ? setPaymentGrids(publishedVersion, scenarios) : resetPaymentGrids()
    );
};

export const paymentGridsMiddleware: Middleware = (store) => (next) => (action: AnyAction) => {
    const result = next(action);

    switch (action.type) {
        // Reload whenever deal data arrives, on app init and on deal refetch.
        case initActionTypes.INIT_PENCIL_SUCCESS:
        case Actions.FETCH_DEAL_SUCCESS: {
            loadPublishedPaymentGrids(store);
            break;
        }
    }

    return result;
};
